import { createContext, useContext, type ReactNode } from "react";
import type { FormulaContextInput } from "./context";
import type { FormulaEvalScope } from "./language";

/**
 * Supplies the formula editor with live evaluation data. Provided by a host that
 * has dummy/real values for the template (the harvest template editor). Absent →
 * the editor shows type-only hints and no value hover. Kept generic so the
 * editor never depends on harvest specifics.
 */
export interface FormulaEvalScopeApi {
  /** The scope to evaluate a formula site against, or null when unavailable. */
  buildScope: (
    context: FormulaContextInput,
    opts?: { tabularRowIndex?: number },
  ) => FormulaEvalScope | null;
  /** Dummy-row count for a tabular attribute, for the editor's row picker. */
  getTabularRowCount: (templateAttributeId: number) => number;
  /** Whether the data the scope is built from is still being fetched. */
  isLoading: boolean;
}

const FormulaEvalScopeContext = createContext<FormulaEvalScopeApi | null>(null);

export function FormulaEvalScopeProvider({
  value,
  children,
}: {
  value: FormulaEvalScopeApi;
  children: ReactNode;
}) {
  return (
    <FormulaEvalScopeContext.Provider value={value}>
      {children}
    </FormulaEvalScopeContext.Provider>
  );
}

export function useFormulaEvalScope(): FormulaEvalScopeApi | null {
  return useContext(FormulaEvalScopeContext);
}
